import type { ColumnDef } from '@tanstack/react-table';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import type { ReactNode } from 'react';

export function createGridRenderCellParams<T extends Record<string, unknown>>(
  row: T,
  col: GridColDef,
  rowIndex: number,
  getRowId?: (row: T) => string | number
): GridRenderCellParams {
  const id = getRowId ? getRowId(row) : (row as { id?: string | number }).id ?? rowIndex;
  const raw = row[col.field as keyof T];
  let value: unknown = raw;
  if (col.valueGetter) {
    const getter = col.valueGetter as (params: {
      id: unknown;
      field: string;
      row: T;
      value: unknown;
    }) => unknown;
    value = getter({
      id,
      field: col.field,
      row,
      value: raw,
    });
  }
  return {
    row,
    id,
    field: col.field,
    formattedValue: '',
    value,
    colDef: col as GridRenderCellParams['colDef'],
    api: {} as GridRenderCellParams['api'],
    rowNode: {} as GridRenderCellParams['rowNode'],
    cellMode: 'view',
    hasFocus: false,
    tabIndex: -1,
  };
}

function defaultFormattedCell<T extends Record<string, unknown>>(
  col: GridColDef,
  params: GridRenderCellParams
): ReactNode {
  const rawValue = params.value;
  if (rawValue == null) return '';
  if (col.valueFormatter) {
    try {
      const formatter = col.valueFormatter as (p: {
        value: unknown;
        row: T;
        field: string;
      }) => unknown;
      const result = formatter({
        value: rawValue,
        row: params.row as T,
        field: col.field,
      });
      if (result != null && typeof result !== 'object') {
        return String(result);
      }
    } catch {
      // fall through
    }
  }
  if (typeof rawValue === 'object') {
    if (rawValue instanceof Date) {
      try {
        return rawValue.toLocaleDateString();
      } catch {
        return String(rawValue);
      }
    }
    if (Array.isArray(rawValue)) {
      return rawValue.join(', ');
    }
    return '';
  }
  return String(rawValue);
}

export function gridColDefToTanStackColumns<T extends Record<string, unknown>>(
  gridCols: GridColDef[],
  options: {
    getRowId?: (row: T) => string | number;
    wrapCellContent?: boolean;
  } = {}
): ColumnDef<T>[] {
  const { getRowId, wrapCellContent = true } = options;

  return gridCols.map((col) => {
    const header = col.headerName ?? col.field;
    const width = typeof col.width === 'number' ? col.width : undefined;
    const minWidth = typeof col.minWidth === 'number' ? col.minWidth : 120;
    const maxWidth = typeof col.maxWidth === 'number' ? col.maxWidth : 2000;
    const flex = typeof col.flex === 'number' ? col.flex : undefined;

    const base = {
      id: col.field,
      header,
      enableSorting: col.sortable !== false,
      enableHiding: col.hideable !== false,
      enableGlobalFilter: col.filterable !== false,
      size: width ?? (flex != null ? Math.round(150 + flex * 40) : 180),
      minSize: minWidth,
      maxSize: maxWidth,
      meta: { gridColDef: col, align: col.align },
    };

    const columnDef = {
      ...base,
      ...(col.valueGetter
        ? {
            accessorFn: (row: T, index: number) => {
              const getter = col.valueGetter as (params: {
                id: unknown;
                field: string;
                row: T;
                value: unknown;
              }) => unknown;
              return getter({
                id: getRowId ? getRowId(row) : ((row as { id?: string | number }).id ?? index),
                field: col.field,
                row,
                value: row[col.field as keyof T],
              });
            },
          }
        : {
            accessorKey: col.field as keyof T & string,
          }),
      cell: ({ row }) => {
        const rowIndex = row.index;
        const original = row.original;
        const params = createGridRenderCellParams(original, col, rowIndex, getRowId);

        let content: ReactNode;
        if (col.renderCell) {
          try {
            content = col.renderCell(params);
          } catch {
            content = defaultFormattedCell(col, params);
          }
        } else {
          content = defaultFormattedCell(col, params);
        }

        if (wrapCellContent) {
          return (
            <Box
              display="flex"
              alignItems="center"
              height="100%"
              width="100%"
              sx={{
                justifyContent:
                  col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start',
              }}
            >
              {content}
            </Box>
          );
        }
        return content;
      },
    } as ColumnDef<T>;

    return columnDef;
  });
}
